import type React from "react";

/**
 * Keyboard movement within a tab list (#11849 Round B): the arrow keys step through its tabs and
 * wrap, Home and End go to the ends. Activation stays manual, on Enter or Space, so moving focus
 * never opens anything. Put it on the element with `role="tablist"`; only that list's own tabs,
 * and only a tab that itself has focus, take part, so a text field inside a tab keeps its arrow
 * keys. A key another handler already claimed (a drag in progress) is left alone.
 */
export function moveTabFocus(event: React.KeyboardEvent<HTMLElement>): void {
  if (event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey)
    return;
  const list = event.currentTarget;
  const tabs = Array.from(
    list.querySelectorAll<HTMLElement>('[role="tab"]')
  ).filter(
    (tab) =>
      tab.closest('[role="tablist"]') === list &&
      !tab.matches(":disabled, [aria-disabled='true']")
  );
  const current = tabs.findIndex((tab) => tab === document.activeElement);
  if (current === -1) return;
  const steps: Record<string, number> = {
    ArrowRight: current + 1,
    ArrowLeft: current - 1,
    Home: 0,
    End: tabs.length - 1,
  };
  const target = steps[event.key];
  if (target === undefined) return;
  event.preventDefault();
  tabs[(target + tabs.length) % tabs.length].focus();
}
